import { Capacitor } from '@capacitor/core';
import { Directory, Filesystem } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

// Hands ONE large file to the system as it is being written (Owner 2026-10-20: an unfinished inspection is
// carried to another device in a file). On a phone the bytes are appended to a file in the app's cache a few
// megabytes at a time — an inspection of 400 photos never sits in memory whole — and the share sheet then
// offers AirDrop, Save to Files, Drive, Mail. In a browser the file downloads. Same discipline as
// src/report/shareAll.ts; the cache folder is emptied at the start of the next export.
export type PackageCode = 'shared' | 'downloaded' | 'cancelled' | 'failed';

export interface PackageSink {
  write(chunk: Uint8Array): Promise<void>;
  finish(): Promise<{ code: PackageCode; detail: string }>;
  abort(): Promise<void>;
}

const FOLDER = 'inspection-moves';
// Appends of about this size: few enough bridge calls, small enough strings.
const FLUSH_BYTES = 3 * 1024 * 1024;

function toBase64(bytes: Uint8Array): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = typeof reader.result === 'string' ? reader.result : '';
      resolve(result.includes(',') ? result.slice(result.indexOf(',') + 1) : result);
    };
    reader.onerror = () => reject(reader.error ?? new Error('read failed'));
    reader.readAsDataURL(new Blob([bytes as BlobPart]));
  });
}

function nativeSink(fileName: string): PackageSink {
  const path = `${FOLDER}/${fileName}`;
  let buffered: Uint8Array[] = [];
  let bufferedBytes = 0;
  let started = false;
  let prepared: Promise<void> | null = null;

  const prepare = () =>
    (prepared ??= Filesystem.rmdir({ path: FOLDER, directory: Directory.Cache, recursive: true })
      .catch(() => undefined)
      .then(() => undefined));

  async function flush(): Promise<void> {
    if (bufferedBytes === 0 && started) return;
    const joined = new Uint8Array(bufferedBytes);
    let at = 0;
    for (const part of buffered) {
      joined.set(part, at);
      at += part.length;
    }
    buffered = [];
    bufferedBytes = 0;
    const data = await toBase64(joined);
    if (!started) {
      await prepare();
      await Filesystem.writeFile({ path, data, directory: Directory.Cache, recursive: true });
      started = true;
    } else await Filesystem.appendFile({ path, data, directory: Directory.Cache });
  }

  return {
    async write(chunk) {
      buffered.push(chunk);
      bufferedBytes += chunk.length;
      if (bufferedBytes >= FLUSH_BYTES) await flush();
    },
    async finish() {
      try {
        await flush();
        const { uri } = await Filesystem.getUri({ path, directory: Directory.Cache });
        await Share.share({ files: [uri] });
        return { code: 'shared', detail: '' };
      } catch (raw) {
        const text = raw instanceof Error ? raw.message : String(raw);
        if (/cancel/i.test(text)) return { code: 'cancelled', detail: '' };
        return { code: 'failed', detail: text };
      }
    },
    async abort() {
      buffered = [];
      bufferedBytes = 0;
      await Filesystem.deleteFile({ path, directory: Directory.Cache }).catch(() => undefined);
    }
  };
}

function browserSink(fileName: string): PackageSink {
  let parts: BlobPart[] = [];
  return {
    async write(chunk) {
      parts.push(chunk as BlobPart);
    },
    async finish() {
      try {
        const url = URL.createObjectURL(new Blob(parts, { type: 'application/zip' }));
        parts = [];
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = fileName;
        anchor.rel = 'noopener';
        document.body.appendChild(anchor);
        anchor.click();
        anchor.remove();
        setTimeout(() => URL.revokeObjectURL(url), 120_000);
        return { code: 'downloaded', detail: '' };
      } catch (raw) {
        return { code: 'failed', detail: raw instanceof Error ? raw.message : String(raw) };
      }
    },
    async abort() {
      parts = [];
    }
  };
}

export function openPackageSink(fileName: string): PackageSink {
  return Capacitor.isNativePlatform() ? nativeSink(fileName) : browserSink(fileName);
}
